import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type DragEvent } from 'react';
import { trpc } from '../api/trpc';
import { wsManager } from '../api/websocket';
import { useToast } from './Toast';
import { useAuth } from '../hooks/useAuth';
import { PastedTextPreview } from './PastedTextPreview';
import { ImagePreviewModal } from './ImagePreviewModal';

interface Props {
  conversationId?: string;
  onNewConversation?: (content: string, fileIds?: string[]) => void;
  placeholder?: string;
  isStreaming?: boolean;
  onSend?: () => void;
  participantCount?: number;
  isNewChat?: boolean;
}

interface UploadedFile {
  name: string;
  fileId?: string;
  status: 'uploading' | 'done' | 'error';
  textContent?: string;
  previewUrl?: string;
}

interface MentionEntry {
  userId: string;
  name: string;
}

export function MessageInput({ conversationId, onNewConversation, placeholder, isStreaming, onSend, participantCount, isNewChat }: Props) {
  const { name: authUserName } = useAuth();
  const [content, setContent] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [mentions, setMentions] = useState<MentionEntry[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [previewText, setPreviewText] = useState<{ text: string; name: string } | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const pasteCounterRef = useRef({ screenshot: 0, text: 0 });
  const sendMessage = trpc.conversations.sendMessage.useMutation();
  const stopGeneration = trpc.conversations.stopGeneration.useMutation();
  const uploadFile = trpc.files.upload.useMutation();
  const isGroupChat = (participantCount ?? 0) > 1;
  const participants = trpc.conversations.getParticipants.useQuery(
    { conversationId: conversationId! },
    { enabled: !!conversationId && isGroupChat },
  );
  const utils = trpc.useUtils();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // Auto-resize textarea to fit content; on New Chat page, cap at viewport bottom
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const handler = () => {
      const topOffset = ta.getBoundingClientRect().top;
      ta.style.height = '0px';
      const naturalHeight = ta.scrollHeight;

      if (isNewChat) {
        const bottomPadding = 32;
        const maxHeight = window.innerHeight - topOffset - bottomPadding;

        if (naturalHeight > maxHeight && maxHeight > 60) {
          ta.style.height = `${maxHeight}px`;
          ta.style.overflowY = 'auto';
          ta.scrollTop = ta.scrollHeight;
        } else {
          ta.style.height = `${naturalHeight}px`;
          ta.style.overflowY = 'hidden';
        }
      } else {
        // Active chat / home page: expand upward, cap at 1/3 viewport
        const maxHeight = Math.floor(window.innerHeight / 3);
        if (naturalHeight > maxHeight) {
          ta.style.height = `${maxHeight}px`;
          ta.style.overflowY = 'auto';
          ta.scrollTop = ta.scrollHeight;
        } else {
          ta.style.height = `${naturalHeight}px`;
          ta.style.overflowY = 'hidden';
        }
      }
    };
    ta.addEventListener('input', handler);
    window.addEventListener('resize', handler);
    handler();
    return () => {
      ta.removeEventListener('input', handler);
      window.removeEventListener('resize', handler);
    };
  }, [isNewChat]);

  // Filter participants for mention autocomplete
  const mentionCandidates = mentionQuery !== null
    ? (participants.data ?? [])
        .filter((p: { userId: string; name: string }) => {
          const q = mentionQuery.toLowerCase();
          return p.name.toLowerCase().includes(q) && !mentions.some(m => m.userId === p.userId);
        })
        .slice(0, 6)
    : [];

  // Reset mention index when candidates change
  useEffect(() => {
    setMentionIndex(0);
  }, [mentionCandidates.length]);

  const sendTypingEvent = useCallback((isTyping: boolean) => {
    if (!conversationId) return;
    if (isTypingRef.current === isTyping) return;
    isTypingRef.current = isTyping;
    wsManager.send({ type: 'typing', conversationId, isTyping, userName: authUserName ?? 'Unknown' });
  }, [conversationId, authUserName]);

  function detectMentionQuery(value: string, cursorPos: number) {
    if (!isGroupChat) { setMentionQuery(null); return; }
    // Look backwards from cursor for an unmatched @
    const before = value.slice(0, cursorPos);
    const atIdx = before.lastIndexOf('@');
    if (atIdx === -1) { setMentionQuery(null); return; }
    // Must be at start or preceded by space/newline
    if (atIdx > 0 && !/\s/.test(before[atIdx - 1]!)) { setMentionQuery(null); return; }
    const query = before.slice(atIdx + 1);
    // No spaces allowed in mention query (once you type a space, autocomplete closes)
    if (/\s/.test(query)) { setMentionQuery(null); return; }
    setMentionQuery(query);
  }

  function handleContentChange(value: string) {
    setContent(value);
    const cursorPos = textareaRef.current?.selectionStart ?? value.length;
    detectMentionQuery(value, cursorPos);

    // Send typing indicator
    sendTypingEvent(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingEvent(false);
    }, 2000);
  }

  function insertMention(userId: string, name: string) {
    const ta = textareaRef.current;
    const cursorPos = ta?.selectionStart ?? content.length;
    const before = content.slice(0, cursorPos);
    const atIdx = before.lastIndexOf('@');
    if (atIdx === -1) return;

    const after = content.slice(cursorPos);
    const newContent = before.slice(0, atIdx) + `@${name} ` + after;
    setContent(newContent);
    setMentions(prev => [...prev.filter(m => m.userId !== userId), { userId, name }]);
    setMentionQuery(null);

    // Restore focus and cursor position
    setTimeout(() => {
      if (ta) {
        const newPos = atIdx + name.length + 2; // @Name + space
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }

  async function handleFileUpload(files: FileList | File[], options?: { textContent?: string }) {
    for (const file of Array.from(files)) {
      const isImage = file.type.startsWith('image/');
      const isText = file.type.startsWith('text/') || file.type === 'application/json' || file.type === 'application/xml' || file.type === 'application/x-yaml';
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined;

      // For text files, read content for snippet preview (unless already provided via options)
      let textContent = options?.textContent;
      if (!textContent && isText && !isImage) {
        try { textContent = (await file.text()).slice(0, 5000); } catch { /* ignore */ }
      }

      const entry: UploadedFile = {
        name: file.name,
        status: 'uploading',
        ...(textContent ? { textContent } : {}),
        ...(previewUrl ? { previewUrl } : {}),
      };
      setAttachedFiles((prev) => [...prev, entry]);

      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = (reader.result as string).split(',')[1] ?? '';
            resolve(result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const result = await uploadFile.mutateAsync({
          conversationId,
          filename: file.name,
          mimeType: file.type,
          content: base64,
        });

        setAttachedFiles((prev) =>
          prev.map((f) => (f.name === file.name && f.status === 'uploading' ? { ...f, status: 'done', fileId: result.fileId } : f)),
        );
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'Upload failed';
        showToast(`Failed to upload ${file.name}: ${reason}`, 'error');
        setAttachedFiles((prev) =>
          prev.map((f) => (f.name === file.name && f.status === 'uploading' ? { ...f, status: 'error' } : f)),
        );
      }
    }
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      void handleFileUpload(e.dataTransfer.files);
    }
  }

  async function send() {
    const doneFiles = attachedFiles.filter((f) => f.status === 'done');
    const fileIds = doneFiles.map((f) => f.fileId).filter((id): id is string => !!id);
    const fileRefs = doneFiles.map((f) => `[Attached: ${f.name}]`).join(' ');
    const trimmed = (content.trim() + (fileRefs ? ` ${fileRefs}` : '')).trim();
    if (!trimmed || sendMessage.isPending) return;

    // Detect mentions: use tracked mentions state, PLUS scan content for @Name
    // patterns matching participants as a fallback (handles manual typing)
    const resolvedMentions = new Map(mentions.map(m => [m.userId, m.name]));
    if (isGroupChat && participants.data) {
      for (const p of participants.data as Array<{ userId: string; name: string }>) {
        if (!resolvedMentions.has(p.userId) && trimmed.includes(`@${p.name}`)) {
          resolvedMentions.set(p.userId, p.name);
        }
      }
    }
    const hasActiveMentions = resolvedMentions.size > 0;
    const mentionUserIds = hasActiveMentions ? [...resolvedMentions.keys()] : undefined;

    setContent('');
    setAttachedFiles([]);
    pasteCounterRef.current = { screenshot: 0, text: 0 };
    setMentions([]);
    setMentionQuery(null);
    sendTypingEvent(false);

    // Reset textarea height after programmatic clear
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.overflowY = 'hidden';
    }

    if (conversationId) {
      // Only signal streaming for regular messages, not notes
      if (!hasActiveMentions) {
        onSend?.();
      }
      await sendMessage.mutateAsync({
        conversationId,
        content: trimmed,
        surface: 'web',
        ...(fileIds.length > 0 ? { fileIds } : {}),
        ...(hasActiveMentions ? { isNote: true, mentionUserIds } : {}),
      });
      await utils.conversations.get.invalidate({ conversationId });
    } else if (onNewConversation) {
      onNewConversation(trimmed, fileIds.length > 0 ? fileIds : undefined);
    }
    textareaRef.current?.focus();
  }

  async function handleStop() {
    if (!conversationId || stopGeneration.isPending) return;
    await stopGeneration.mutateAsync({ conversationId });
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const cd = e.clipboardData;

    // Priority 1: Files from clipboard (file explorer copy, or fallback for images)
    if (cd.files.length > 0) {
      e.preventDefault();
      void handleFileUpload(Array.from(cd.files));
      return;
    }

    // Priority 2: Image items (screenshots from Snipping Tool, etc.)
    const imageItem = Array.from(cd.items).find(
      item => item.kind === 'file' && item.type.startsWith('image/')
    );
    if (imageItem) {
      e.preventDefault();
      const blob = imageItem.getAsFile();
      if (blob) {
        const ext = blob.type.split('/')[1] || 'png';
        pasteCounterRef.current.screenshot++;
        const name = `Pasted screenshot ${pasteCounterRef.current.screenshot}`;
        const file = new File([blob], `${name}.${ext}`, { type: blob.type });
        void handleFileUpload([file]);
      }
      return;
    }

    // Priority 3: Long text (> 4096 characters) → treat as text file attachment
    const text = cd.getData('text/plain');
    if (text && text.length > 4096) {
      e.preventDefault();
      pasteCounterRef.current.text++;
      const name = `Pasted text ${pasteCounterRef.current.text}`;
      const file = new File([text], `${name}.txt`, { type: 'text/plain' });
      void handleFileUpload([file], { textContent: text });
      return;
    }

    // Default: short text — let browser handle normally
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Mention autocomplete navigation
    if (mentionQuery !== null && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        const candidate = mentionCandidates[mentionIndex] as { userId: string; name: string } | undefined;
        if (candidate) {
          insertMention(candidate.userId, candidate.name);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === 'Escape' && isStreaming) {
      e.preventDefault();
      void handleStop();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <>
    <div className="message-input-bar">
      <div
        className={`message-input-bar-wrap ${dragOver ? 'message-input-dragover' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={(e) => { if ((e.target as HTMLElement).closest('button, .attachment-card')) return; textareaRef.current?.focus(); }}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="file-input-hidden"
          onChange={(e) => { if (e.target.files) void handleFileUpload(e.target.files); }}
          multiple
        />
        <button
          className="input-plus-btn"
          title="Add files"
          aria-label="Add files"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M13.5 7.5l-5.25 5.25a3.18 3.18 0 0 1-4.5-4.5L9.5 2.5a2.12 2.12 0 0 1 3 3L6.75 11.25a1.06 1.06 0 0 1-1.5-1.5L10.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="message-input-content" style={{ position: 'relative' }}>
          {attachedFiles.length > 0 && (
            <div className="attached-files">
              {attachedFiles.map((f, i) => {
                const handleClick = () => {
                  if (f.previewUrl) setPreviewImage({ url: f.previewUrl, name: f.name });
                  else if (f.textContent) setPreviewText({ text: f.textContent, name: f.name });
                };
                const statusClass = f.status === 'uploading' ? ' attachment-card-uploading' : f.status === 'error' ? ' attachment-card-error' : '';
                return (
                  <div key={i} className={`attachment-card${statusClass}`} onClick={handleClick}>
                    {f.previewUrl ? (
                      <img src={f.previewUrl} alt={f.name} />
                    ) : f.textContent ? (
                      <div className="attachment-card-text">{f.textContent.slice(0, 200)}</div>
                    ) : (
                      <div className="attachment-card-file">
                        <svg className="attachment-card-file-icon" width="20" height="20" viewBox="0 0 16 16" fill="none">
                          <path d="M9 1.5H4.5A1.5 1.5 0 003 3v10a1.5 1.5 0 001.5 1.5h7A1.5 1.5 0 0013 13V5.5L9 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                          <path d="M9 1.5V5.5H13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="attachment-card-file-name">{f.name}</span>
                      </div>
                    )}
                    {f.status === 'uploading' && <span className="attachment-card-spinner"><span className="tool-call-spinner" /></span>}
                    <button className="attachment-card-remove" onClick={(e) => { e.stopPropagation(); setAttachedFiles((prev) => prev.filter((_, j) => j !== i)); }}>×</button>
                  </div>
                );
              })}
            </div>
          )}
          <textarea
            ref={textareaRef}
            className="message-input"
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder ?? 'Start a new chat in this project…'}
            rows={1}
            disabled={sendMessage.isPending}
          />
          {/* Mention autocomplete dropdown */}
          {mentionQuery !== null && mentionCandidates.length > 0 && (
            <div className="mention-dropdown" ref={mentionDropdownRef}>
              {mentionCandidates.map((p: { userId: string; name: string; email: string }, i: number) => (
                <button
                  key={p.userId}
                  className={`mention-dropdown-item ${i === mentionIndex ? 'mention-dropdown-item-active' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); insertMention(p.userId, p.name); }}
                  onMouseEnter={() => setMentionIndex(i)}
                >
                  <div className="mention-dropdown-avatar">
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="mention-dropdown-info">
                    <span className="mention-dropdown-name">{p.name}</span>
                    <span className="mention-dropdown-email">{p.email}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {isStreaming ? (
          <button
            className="btn-stop"
            onClick={() => { void handleStop(); }}
            disabled={stopGeneration.isPending}
            aria-label="Stop generation"
            title="Stop generation (Esc)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="4" width="16" height="16" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            className="btn-send"
            onClick={() => { void send(); }}
            disabled={!content.trim() && attachedFiles.filter((f) => f.status === 'done').length === 0 || sendMessage.isPending}
            aria-label="Send"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        )}
      </div>
    </div>
    {previewText && (
      <PastedTextPreview
        text={previewText.text}
        filename={previewText.name}
        onClose={() => setPreviewText(null)}
      />
    )}
    {previewImage && (
      <ImagePreviewModal
        url={previewImage.url}
        filename={previewImage.name}
        onClose={() => setPreviewImage(null)}
      />
    )}
    </>
  );
}
